/** Teleop command config/timing. Stick axes internally −1…1; wire sends SI via slider caps. */

import type {
  DriveCommand,
  PostureKey,
  PostureWireType,
  VelocityDemand,
} from "../../types/commands";

export interface TeleopConfig {
  emitIntervalMs: number;
  deadZone: number;
  epsilon: number;
  /** Discrete input: full travel along an axis (see teleop-input's getDiscreteVelocity). */
  discreteNorm: Record<DriveCommand, Required<VelocityDemand>>;
}

export const TELEOP: TeleopConfig = {
  emitIntervalMs: 40,
  deadZone: 0.15,
  epsilon: 0.02,
  discreteNorm: {
    // steer_angle mirrors vy on the two strafe keys — a rover has no lateral axis of
    // its own, so its steering demand rides vy through the shared input domain (see
    // MotionStream/clampVelocity's `steer_angle ?? vy` fallback); explicit here only
    // so every entry satisfies `Required<VelocityDemand>`.
    forward: { vx: 1, vy: 0, vyaw: 0, steer_angle: 0 },
    back: { vx: -1, vy: 0, vyaw: 0, steer_angle: 0 },
    left: { vx: 0, vy: 1, vyaw: 0, steer_angle: 1 },
    right: { vx: 0, vy: -1, vyaw: 0, steer_angle: -1 },
    yaw_left: { vx: 0, vy: 0, vyaw: 1, steer_angle: 0 },
    yaw_right: { vx: 0, vy: 0, vyaw: -1, steer_angle: 0 },
  },
};

export interface PostureConfig {
  cooldownMs: number;
  /** UI key → wire `type`. */
  types: Record<PostureKey, PostureWireType>;
}

/** Edge-triggered posture commands (send-posture, one shot per press). */
export const POSTURE: PostureConfig = {
  cooldownMs: 300,
  types: {
    stand_up: "posture.stand_up",
    stand_down: "posture.stand_down",
  },
};

export interface CargoConfig {
  cooldownMs: number;
  pendingTimeoutMs: number;
}

/** Edge-triggered cargo open (send-cargo, one shot per box). */
export const CARGO: CargoConfig = {
  cooldownMs: 300,
  pendingTimeoutMs: 3000,
};
